import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import cs from 'classnames';
import { Helmet } from 'react-helmet-async';

import { usePageScroll } from '../../../contexts/PageScrollContext';
import { useSearch } from '../../../hooks/useSearch';
import { useSelectedCategory } from '../../../hooks/useSelectedCategory';
import { useInternalRoutes } from '../../../hooks/useInternalRoutes';
import { usePagination } from '../../../hooks/usePagination';
import { CategorySlug } from '../../../config';
import { useBiLogger } from '../../../contexts/BILoggerContext';
import { useInitialState } from '../../../contexts/InitialStateContext';
import { joinWithSlashes } from '../../../utils/joinWithSlashes';

import s from './PageLink.scss';

interface PageLinkProps {
  page: number;
  isPrev?: boolean;
  isNext?: boolean;
}

export const PageLink: FC<PageLinkProps> = ({
  page,
  isPrev,
  isNext,
  children,
}) => {
  const { isSearchActive, criteria } = useSearch();
  const category = useSelectedCategory();
  const routes = useInternalRoutes();
  const { currentPage } = usePagination();
  const { scrollToTop } = usePageScroll();
  const { reportPaginationClick } = useBiLogger();
  const { baseUrl } = useInitialState();
  const isActive = currentPage === page;

  const handleLinkClick = () => {
    let actionValue = page.toString();
    if (isPrev) {
      actionValue = 'left arrow';
    } else if (isNext) {
      actionValue = 'right arrow';
    }
    reportPaginationClick({ actionValue });
    scrollToTop({ behavior: 'instant' });
  };

  const absoluteUrl = joinWithSlashes(
    baseUrl,
    isSearchActive
      ? routes.search.build(criteria, page)
      : routes.category.build(category?.slug ?? CategorySlug.DEFAULT, page),
  );

  return (
    <>
      {(isPrev || isNext) && (
        <Helmet>
          {isPrev && <link rel="prev" href={absoluteUrl} />}
          {isNext && <link rel="next" href={absoluteUrl} />}
        </Helmet>
      )}
      <Link
        to={absoluteUrl}
        className={cs(s.link, {
          [s.noUnderlineOnHover]: isPrev || isNext,
          [s.active]: !isPrev && !isNext && isActive,
        })}
        onClick={handleLinkClick}
      >
        {children}
      </Link>
    </>
  );
};
